import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import heroImg from '../../assets/hero-clio-panorama.jpg';
import AddressAutocompleteInput from '../../components/AddressAutocompleteInput';
import SelectedModelInput from '../../components/SelectedModelInput';

import { useUser } from '../../utils/hook/UserContext';

import { useAxios } from '../../utils/hook/useAxios';

import './LouerVoiture.css';

// ── Estimation engine (inchangé) ─────────────────────────────────────────────

const LUXURY_BRANDS = new Set(
  'bmw,mercedes,mercedes-benz,audi,porsche,tesla,lexus,maserati,ferrari,lamborghini,bentley,rolls-royce,jaguar,land rover,cadillac,infiniti,genesis,aston martin,bugatti,mclaren,range rover'.split(
    ','
  )
);
const PREMIUM_BRANDS = new Set(
  'volkswagen,vw,volvo,alfa romeo,skoda,mini,seat,jeep,toyota,honda,hyundai,kia,mazda,subaru,saab,acura,lincoln'.split(
    ','
  )
);
const ECONOMY_BRANDS = new Set(['dacia', 'lada', 'smart', 'daewoo', 'tata', 'chery', 'geely']);

const CITY_DEMAND = [
  { keys: ['paris'], mult: 1.4 },
  { keys: ['nice', 'cannes', 'monaco', 'antibes', 'saint-tropez'], mult: 1.32 },
  { keys: ['lyon', 'marseille'], mult: 1.22 },
  {
    keys: [
      'bordeaux',
      'toulouse',
      'nantes',
      'lille',
      'strasbourg',
      'rennes',
      'montpellier',
      'grenoble',
      'toulon',
      'aix',
    ],
    mult: 1.13,
  },
  { keys: ['genève', 'geneva', 'zürich', 'zurich', 'lausanne'], mult: 1.35 },
];

const AGE_MULT = [
  { from: 2023, mult: 1.22 },
  { from: 2020, mult: 1.08 },
  { from: 2016, mult: 0.88 },
  { from: 0, mult: 0.72 },
];

const KM_MULT = [1.18, 1.06, 0.96, 0.85, 0.74, 0.62];

function estimateRevenue({ marque, locationName, annee, km }) {
  const brand = (marque || '').toLowerCase();
  const baseDaily = LUXURY_BRANDS.has(brand)
    ? 92
    : PREMIUM_BRANDS.has(brand)
      ? 66
      : ECONOMY_BRANDS.has(brand)
        ? 36
        : 50;

  const city = (locationName || '').toLowerCase();
  let locMult = 1.0;
  for (const { keys, mult } of CITY_DEMAND) {
    if (keys.some((k) => city.includes(k))) {
      locMult = mult;
      break;
    }
  }
  if (locMult === 1.0 && city.length > 0) locMult = 1.04;

  const year = parseInt(annee) || 2020;
  const ageMult = AGE_MULT.find((r) => year >= r.from)?.mult ?? 0.72;
  const kmMult = KM_MULT[parseInt(km)] ?? 0.96;

  const daily = Math.round(baseDaily * locMult * ageMult * kmMult);
  return {
    daily,
    min: Math.round(daily * 14),
    max: Math.round(daily * 22),
    mid: Math.round(daily * 18),
    segment: LUXURY_BRANDS.has(brand)
      ? 'Premium'
      : PREMIUM_BRANDS.has(brand)
        ? 'Confort'
        : ECONOMY_BRANDS.has(brand)
          ? 'Économique'
          : 'Standard',
  };
}

// ── Données d'affichage ──────────────────────────────────────────────────────

const YEARS = Array.from({ length: 17 }, (_, i) => String(2026 - i)); // 2026 → 2010

const KM_OPTIONS = [
  ['0', '0-15 000 km'],
  ['1', '15-50 000 km'],
  ['2', '50-100 000 km'],
  ['3', '100-150 000 km'],
  ['4', '150-200 000 km'],
  ['5', '+200 000 km'],
];

const AVATARS = ['A', 'M', 'S', 'K']; // remplace par de vraies photos si tu en as

const ECO = [
  { icon: 'bi-leaf', c: 'teal', t: 'Économique', s: 'Optimisez vos coûts' },
  { icon: 'bi-shield-check', c: 'orange', t: 'Fiable', s: 'Des véhicules vérifiés' },
  { icon: 'bi-leaf', c: 'teal', t: 'Écoresponsable', s: 'Partagez, réduisez, préservez' },
];

// ────────────────────────────────────────────────────────────────────────────

function LouerVoiturePage() {
  const [selectedAnnee, setSelectedAnnee] = useState('2024');
  const [selectedKm, setSelectedKm] = useState('2');
  const [resetInput] = useState(true);
  const [locationCenter, setLocationCenter] = useState([0, 0]);
  const [modelId, setModelId] = useState(null);
  const [modelName, setModelName] = useState(null);
  const [modelMarque, setModelMarque] = useState(null);
  const [locationPlaceName, setLocationPlaceName] = useState('');
  const axios = useAxios();
  const { user, loading, isAuthenticated } = useUser();

  const handleLocationSelection2 = (locationAddress) => {
    setLocationCenter(locationAddress.location);
    setLocationPlaceName(locationAddress.name);
  };

  const handleModelSelection = (model) => {
    setModelId(model.id);
    setModelName(model.modele);
    setModelMarque(model.marque);
  };

  const estimate = useMemo(
    () =>
      estimateRevenue({
        marque: modelMarque,
        locationName: locationPlaceName,
        annee: selectedAnnee,
        km: selectedKm,
      }),
    [modelMarque, locationPlaceName, selectedAnnee, selectedKm]
  );

  const hasInput = modelId || locationPlaceName;

  function handleCommencer() {
    const coords = locationCenter[1].toString() + ',' + locationCenter[0].toString();
    const data = {
      modeleId: modelId,
      CoordonatesCity: coords,
      kilometrage: selectedKm,
      annee: selectedAnnee,
      userId: user?.id,
    };
    window.localStorage.setItem('VehicleStorage', JSON.stringify(data));

    axios
      .post('/vehicles', data)
      .then((response) => {
        const stored = { ...data, id: response.data.id, vehiculeId: response.data.id };
        window.localStorage.setItem('VehicleStorage', JSON.stringify(stored));
        window.location.href = '/confirmmodel/' + response.data.id;
      })
      .catch((error) => {
        console.error('Erreur de requête:', error);
      });
  }

  return (
    <section className="lv-page">
      {/* Photo de fond (fondue vers le blanc à gauche et en bas) */}
      <div className="lv-photo">
        <img src={heroImg} alt="Renault Clio rouge devant un panorama au lever du soleil" />
      </div>

      <div className="lv-inner">
        {/* ================= COLONNE GAUCHE ================= */}
        <div className="lv-left">
          <div className="lv-ai-row">
            <span className="lv-ai-pill">
              <i className="bi bi-stars" /> Propulsé par l'IA
            </span>
            <span className="lv-ai-text">
              Des recommandations personnalisées pour votre trajet idéal
            </span>
          </div>

          <h1 className="lv-title">
            Complétez vos revenus en <em>partageant votre voiture</em>
          </h1>

          <p className="lv-subtitle">
            Flexible, simple et sécurisé. Trouvez les bonnes personnes et rentabilisez votre
            véhicule en quelques clics.
          </p>

          <div className="lv-card">
            {/* Onglets */}
            <div className="lv-tabs">
              <Link to="/" className="lv-tab">
                <i className="bi bi-car-front" /> Louer un véhicule
              </Link>
              <span className="lv-tab is-active">
                <i className="bi bi-person" /> Proposer mon véhicule
              </span>
              <span className="lv-tab">
                <i className="bi bi-shield-check" /> Sécurisé &amp; vérifié
              </span>
            </div>

            <div className="lv-form">
              {/* Modèle */}

              <div className="lv-box-body" style={{ marginBottom: '0.5rem' }}>
                <SelectedModelInput
                  handleSelectModel={handleModelSelection}
                  resetInput={resetInput}
                />
              </div>

              {/* Localisation */}
              <div style={{ marginBottom: '0.23rem' }}>
                {/*  <AutocompleteMapInput handleSelectLocation={handleLocationSelection2} />*/}
                <AddressAutocompleteInput handleSelectLocation={handleLocationSelection2} />
              </div>

              {/* Année / Kilométrage */}
              <div className="lv-pair">
                <div className="lv-box">
                  <span className="lv-box-icon">
                    <i className="bi bi-calendar3" />
                  </span>
                  <div className="lv-box-body">
                    <label htmlFor="lv-annee">Année</label>
                    <select
                      id="lv-annee"
                      value={selectedAnnee}
                      onChange={(e) => setSelectedAnnee(e.target.value)}
                    >
                      {YEARS.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="lv-box">
                  <span className="lv-box-icon">
                    <i className="bi bi-speedometer2" />
                  </span>
                  <div className="lv-box-body">
                    <label htmlFor="lv-km">Kilométrage</label>
                    <select
                      id="lv-km"
                      value={selectedKm}
                      onChange={(e) => setSelectedKm(e.target.value)}
                    >
                      {KM_OPTIONS.map(([v, label]) => (
                        <option key={v} value={v}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Estimation */}
              <div className="lv-summary">
                <i className="bi bi-graph-up-arrow" />
                {hasInput ? (
                  <>
                    <strong>{estimate.mid} € / mois</strong>
                    <span className="lv-dot" />
                    <span>
                      {estimate.min} – {estimate.max} € estimé
                    </span>
                    <button
                      type="button"
                      className="lv-info"
                      data-bs-toggle="modal"
                      data-bs-target="#ModalEstimationInfos"
                      aria-label="Comment est calculée l'estimation ?"
                    >
                      <i className="bi bi-info-circle" />
                    </button>
                    <span className="lv-best">
                      <i className="bi bi-car-front" /> {estimate.segment} · {estimate.daily} €/jour
                    </span>
                  </>
                ) : (
                  <span>Sélectionnez un modèle et une ville pour voir votre estimation</span>
                )}
              </div>

              {/* Bandeau de confiance */}
              <div className="lv-strip">
                <div>
                  <i className="bi bi-cpu" />
                  <span>
                    <b>Recommandations personnalisées</b>
                    <small>Selon vos critères et vos habitudes</small>
                  </span>
                </div>
                <div>
                  <i className="bi bi-shield-check" />
                  <span>
                    <b>Paiement sécurisé</b>
                    <small>100% protégé</small>
                  </span>
                </div>
                <div>
                  <i className="bi bi-person-check" />
                  <span>
                    <b>Véhicules vérifiés</b>
                    <small>et assurés</small>
                  </span>
                </div>
              </div>

              <button className="lv-cta" onClick={handleCommencer} disabled={!modelId}>
                <i className="bi bi-search" /> Commencer <i className="bi bi-arrow-right" />
              </button>
              {!modelId && <p className="lv-hint">Sélectionnez d'abord un modèle de voiture</p>}
              <Link to="/annonce-express" className="lv-cta">
                <i className="bi bi-lightning-charge" /> Créer mon annonce en 60 secondes avec l'IA
              </Link>
            </div>
          </div>

          {/* Preuve sociale */}
          <div className="lv-proof">
            <div className="lv-avatars">
              {AVATARS.map((a) => (
                <span key={a}>{a}</span>
              ))}
            </div>
            <span className="lv-stars">
              <i className="bi bi-star-fill" />
              <i className="bi bi-star-fill" />
              <i className="bi bi-star-fill" />
              <i className="bi bi-star-fill" />
              <i className="bi bi-star-fill" />
            </span>
            <strong>4.8/5</strong>
            <span className="lv-muted">(2 500+)</span>
            <span className="lv-sep" />
            <span className="lv-muted">Plus de 10 000 conducteurs nous font confiance</span>
          </div>

          <p className="lv-axa">Location de voiture entre particuliers et pros assurée par AXA</p>
        </div>

        {/* ================= COLONNE DROITE ================= */}
        <div className="lv-right">
          <p className="lv-script">
            Votre voiture
            <br />
            gagne de la valeur
            <svg viewBox="0 0 240 20" className="lv-underline" aria-hidden="true">
              <path
                d="M4 14 C 60 4, 150 4, 236 2"
                fill="none"
                stroke="#ff5f0f"
                strokeWidth="5"
                strokeLinecap="round"
              />
            </svg>
            <svg viewBox="0 0 90 60" className="lv-arrow" aria-hidden="true">
              <path
                d="M70 4 C 78 30, 50 50, 10 46 M10 46 l14 -10 M10 46 l16 6"
                fill="none"
                stroke="#fff"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </p>

          <article className="lv-reco">
            <header>
              <span className="lv-reco-title">
                <i className="bi bi-stars" /> Recommandé par l'IA
              </span>
              <span className="lv-reco-tag">Meilleure correspondance</span>
            </header>

            <div className="lv-reco-body">
              <img src={heroImg} alt="" />
              <div className="lv-reco-info">
                <h3>{modelName || 'Renault Clio'}</h3>
                <ul>
                  <li>
                    <i className="bi bi-person" /> 5 places
                  </li>
                  <li>
                    <i className="bi bi-gear" /> Manuelle
                  </li>
                  <li>
                    <i className="bi bi-fuel-pump" /> Essence
                  </li>
                </ul>
                <div className="lv-reco-price">
                  <div>
                    <small>À partir de</small>
                    <strong>
                      {hasInput ? estimate.daily : 25} € <span>/ jour</span>
                    </strong>
                  </div>
                  <span className="lv-rating">
                    <i className="bi bi-star-fill" /> 4.8 <small>(124 avis)</small>
                  </span>
                </div>
              </div>
              <Link to="/search" className="lv-round-btn" aria-label="Voir l'offre">
                <i className="bi bi-arrow-right" />
              </Link>
            </div>
          </article>

          <div className="lv-eco">
            {ECO.map((e) => (
              <div key={e.t}>
                <span className={`lv-eco-icon ${e.c}`}>
                  <i className={`bi ${e.icon}`} />
                </span>
                <span>
                  <b>{e.t}</b>
                  <small>{e.s}</small>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal infos (inchangée) */}
      <div
        className="modal fade"
        id="ModalEstimationInfos"
        tabIndex="-1"
        aria-labelledby="ModalEstimationInfosLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold" id="ModalEstimationInfosLabel">
                Comment est calculée l'estimation ?
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body" style={{ fontSize: 14 }}>
              <p>L'estimation est calculée en combinant quatre facteurs :</p>
              <ul>
                <li>
                  <strong>Segment du véhicule</strong> — Premium, Confort, Standard ou Économique
                  selon la marque.
                </li>
                <li>
                  <strong>Demande locale</strong> — les grandes villes (Paris, Nice, Lyon…) génèrent
                  des tarifs plus élevés.
                </li>
                <li>
                  <strong>Année</strong> — un véhicule récent se loue plus cher.
                </li>
                <li>
                  <strong>Kilométrage</strong> — moins de km = meilleure valeur perçue.
                </li>
              </ul>
              <p className="text-muted small mb-0">
                L'estimation suppose ~18 jours de location par mois. Vos revenus réels dépendent de
                votre disponibilité, de la saison et de la demande dans votre zone.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary btn-sm" data-bs-dismiss="modal">
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LouerVoiturePage;
